import React, { useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Page, Button, Input, Checkbox, alert } from 'equasion-lib';
import { Formik, Form } from 'formik';
import { placeholderReceivingValidation } from './PlaceholderReceiving.validation';
import { useRootStore } from '@/lib/providers/RootProvider';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';

function PlaceholderReceiving({ purchaseOrder, receivePlaceholderInventory }) {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const navigate = useNavigate();
  const [itemsAreMissing, setItemsAreMissing] = useState(false);
  const {
    id: purchaseOrderId,
    clientPurchaseOrderNumber,
    vendor: { companyName },
    orderItems
  } = purchaseOrder;
  const totalQuantity = orderItems.reduce((acc, item) => acc + item.quantity, 0);

  const submitReceive = async (values, actions) => {
    const { setSubmitting } = actions;
    try {
      await receivePlaceholderInventory({
        variables: {
          id: purchaseOrderId,
          orderItems: values.orderItems.map((item) => ({
            productId: item.productId,
            quantity: item.quantity
          })),
          companyId
        }
      });
      alert('Received', 'Placeholder inventory received', 4, 'success');
      navigate('/placeholder-purchase-orders?tab=stocking');
    } catch (e: any) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Failed to receive';
      alert('Error', msg, 6, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page className="unserialized-receiving">
      <div className="action-top-bar">
        <Button
          type="secondary"
          iconBefore={<FiChevronLeft />}
          onClick={() => navigate('/placeholder-purchase-orders?tab=receiving')}
        >
          Back
        </Button>
        <div className="order-details">
          <h1>Receiving (Unserialized)</h1>
        </div>
      </div>
      <div className="content">
        <div className="order-details">
          <div className="order-number">
            <span>PO #{clientPurchaseOrderNumber}</span>
          </div>
          <div className="order-info">
            <span>
              {totalQuantity} {totalQuantity === 1 ? 'Item' : 'Items'}
            </span>
            <span>•</span>
            <span className="company-name">{companyName}</span>
          </div>
        </div>
        <div
          style={{
            fontSize: '1.4rem',
            marginBottom: '1.6rem',
            color: 'var(--y500)'
          }}
        >
          These are DEA-scheduled items with no EPCIS. Real serial numbers will be
          captured at Picking.
        </div>
        <Checkbox
          form={false}
          label="PO has missing items"
          defaultValue={itemsAreMissing}
          value={itemsAreMissing}
          onChange={({ value }) => setItemsAreMissing(value)}
          className="items-are-missing-checkbox"
        />
        {itemsAreMissing && (
          <div
            style={{
              color: 'var(--n100)',
              fontSize: '1.3rem',
              margin: '-1rem 0 1.6rem 0.5rem'
            }}
          >
            Check the items below that are missing. Enter quantities only for the items
            you actually received. Missing quantities will be split into a separate
            awaiting-EPCIS sub-order.
          </div>
        )}
        <Formik
          initialValues={{
            orderItems: orderItems.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
              missing: false
            }))
          }}
          validationSchema={placeholderReceivingValidation()}
          validateOnBlur={false}
          onSubmit={submitReceive}
        >
          {({ isSubmitting, values }) => (
            <Form className="unserialized-receiving-form">
              <div className="flex space-between order-items-header">
                <span>
                  {orderItems.length === 1
                    ? `Item (${orderItems.length})`
                    : `Items (${orderItems.length})`}
                </span>
                <span>Qty</span>
              </div>
              {orderItems.map((item, i) => (
                <div key={item.productId}>
                  <div>
                    <div>
                      {itemsAreMissing && (
                        <Checkbox name={`orderItems.${i}.missing`} label="" />
                      )}
                      <WarehouseProductDisplay product={item.product} />
                    </div>
                    <span className="quantity">{item.quantity}</span>
                  </div>
                  {values.orderItems[i].missing && (
                    <Input
                      name={`orderItems.${i}.quantity`}
                      label="Quantity Received"
                      inputType="number"
                      inputMode="numeric"
                      autoFocus
                      onFocus={(e) => e.target.select()}
                    />
                  )}
                </div>
              ))}
              <Button type="primary" isSubmit disabled={isSubmitting} fullWidth>
                {itemsAreMissing && values.orderItems.some((item) => item.missing)
                  ? 'Receive And Split Missing'
                  : 'Receive'}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </Page>
  );
}

export default PlaceholderReceiving;
